import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "mb-x6 mt-x3 text-center" }
const _hoisted_2 = { class: "mb-x4 flex sm:flex-col flex-wrap justify-center items-center gap-y-x2" }
const _hoisted_3 = { class: "flex items-center gap-x-x2" }
const _hoisted_4 = {
  key: 0,
  class: "w-full text-center"
}
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "flex"
}

import { useCheckout } from '@/b2c/checkout/useCheckout';
import { computed, ref, watch } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import {
    AddInvoiceAddressToBasketRequest,
} from '@/b2c/shared/api/server-apis';
import useCountries from '@/shared/address/useCountries';
import { useDeliverables } from '@/b2c/basket/useDeliverables';
import { useRequiredForm } from '@/shared/form/useRequiredForm';
import { isEmpty, pickBy } from 'lodash';
import { BaseAddressFormValues } from '@/b2c/shared/address/ui/base-address-form.types';
import BaseAddressForm from '@/b2c/shared/address/ui/BaseAddressForm.vue';
import CheckoutPaymentOptions from '@/b2c/checkout/ui/payment/CheckoutPaymentOptions.vue';
import siteContextService from '@/b2c/shared/site-context.service';
import authStore from '@/shared/auth/auth.store';
import CheckoutLoginLink from '@/b2c/checkout/ui/CheckoutLoginLink.vue';
import ContactInfoForm from '@/b2c/shared/address/ui/ContactInfoForm.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutStepAddress',
  setup(__props) {

const labels = useBlockLabels();

const {
    updateStep,
    isLastStep,
    deliverableCount,
    addInvoiceAddress,
    invoiceAddress,
    isAddressStep,
    setPaymentAddressValidationHandler,
    setIsPaymentReady,
    isClickAndCollectOnly,
} = useCheckout();

const { customerDeliveryPostalCode } = useDeliverables();

const {
    siteCountryCode,
    siteCountryName,
} = useCountries();

const isSignedIn = computed(() => authStore.isSignedIn);

const pending = ref<boolean>(false);

const resetToAddressStep = () => {
    updateStep(0);
};

const buyer = computed(() => {
    return invoiceAddress.value?.buyer;
});
const address = computed(() => {
    return invoiceAddress.value?.address;
});
const buyerContactInformation = computed(() => {
    return invoiceAddress.value?.buyerContactInformation;
});

const validationSchema = computed(dynamicValidationSchema);
const postalCodeValidationRegEx = computed<string>(() => siteContextService.country.postalCodeRegex);

const initialValues = ():Partial<BaseAddressFormValues> => ({
    companyName: '',
    vatNumber: '',
    ...buyer.value,
    ...address.value,
    ...buyerContactInformation.value,
    country: siteCountryName.value,
    extraInformation: address.value?.street2,
    postalCode: address.value?.postalCode || customerDeliveryPostalCode.value || '',
    alternativePhoneNumber: buyerContactInformation.value?.alternateNumber,
    subscribeToNewsletter: invoiceAddress.value?.signUpNewsLetter,
    email: invoiceAddress.value?.buyerContactInformation?.emailAddress,
    confirmEmail: invoiceAddress.value?.buyerContactInformation?.emailAddress,
});

// TODO: Figure out if some of this can be reused to handle prefilled info after login
// function perhapsReuseOnUserLogin(customerData: Partial<CustomerAddressSuggestionModel>) {
//     resetForm();
//     setValues({
//         ...customerData,
//         ...customerData.address,
//         phoneNumber: customerData.phoneNumber || '',
//         alternativePhoneNumber: customerData.alternativePhoneNumber || '',
//         countryCode: siteCountryCode.value,
//         country: siteCountryName.value,
//         customerType: CustomerType.B2C,
//     });
// }

const gotoDeliverables = ():void => {
    updateStep(1);
};

const { handleSubmit, setFieldValue, validate } = useRequiredForm({
    validationSchema,
    initialValues: initialValues(),
});

function dynamicValidationSchema() {
    return {
        companyName: {
            max: 60,
        },
        firstName: {
            required: true,
        },
        lastName: {
            required: true,
        },
        postalCode: {
            postalCode: postalCodeValidationRegEx.value,
            required: true,
        },
        city: {
            required: true,
        },
        street: {
            required: true,
        },
        country: {
            required: true,
        },
        phoneNumber: {
            required: true,
            phone: true,
        },
        alternativePhoneNumber: {
            phone: true,
        },
        email: {
            required: true,
            email: true,
        },
        confirmEmail: {
            required: true,
            confirmEmail: '@email',
        },
    };
}

// Wait for country names to be loaded
watch(siteCountryName, (name) => {
    setFieldValue('country', name);
}, { immediate: true });

const submit = handleSubmit(async(form) => {
    if (pending.value) {
        return;
    }
    pending.value = true;

    const buyer = {
        firstName: form.firstName || '',
        lastName: form.lastName || '',
        companyName: form.companyName,
        vatNumber: form.vatNumber,
    };

    const address = {
        city: form.city || '',
        hasLift: form.hasLift,
        otherCountryName: '',
        postalCode: form.postalCode || '',
        unknownPostalCode: form.isUnknownPostalCode,
        street: form.street || '',
        street2: form.extraInformation,
    };

    const buyerContactInformation = {
        emailAddress: form.email,
        phoneNumber: form.phoneNumber,
        alternateNumber: form.alternativePhoneNumber,
    };

    const hasBuyerInfo = !isEmpty(pickBy(buyer));
    const hasAddressInfo = !isEmpty(pickBy(address));
    const hasContactInfo = !isEmpty(pickBy(buyerContactInformation));

    const request: AddInvoiceAddressToBasketRequest = {
        invoiceAddress: {
            buyer: hasBuyerInfo ? buyer : undefined,
            address: hasAddressInfo ? {
                ...address,
                country: siteCountryName.value,
                countryCode: siteCountryCode.value,
            } : undefined,
            buyerContactInformation: hasContactInfo ? buyerContactInformation : undefined,
            signUpNewsLetter: form.subscribeToNewsletter,
        },
    };

    try {
        await addInvoiceAddress(request);
        customerDeliveryPostalCode.value = form.postalCode ?? '';
        if (isLastStep.value) {
            setIsPaymentReady(true);
        } else {
            gotoDeliverables();
        }
    } catch (err) {
        setIsPaymentReady(false);
    } finally {
        pending.value = false;
    }
});

const paymentHandler = async() => {
    const result = await validate({ mode: 'silent' });
    if (result.valid) {
        submit();
    } else {
        resetToAddressStep();
    }
    return result.valid;
};

setPaymentAddressValidationHandler(paymentHandler);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("li", null, [
    _createElementVNode("h1", _hoisted_1, _toDisplayString(_unref(labels).addressHeader), 1),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, _toDisplayString(_unref(labels).invoiceAddressChooseAnotherDeliveryAddressLaterInfo), 1),
      _createElementVNode("div", _hoisted_3, [
        (!_unref(isSignedIn))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", null, _toDisplayString(_unref(labels).invoiceAddressAlreadyCustomerQuestion), 1),
              _createVNode(CheckoutLoginLink, { class: "c-button c-button--secondary-dark capitalize" })
            ], 64))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("form", {
      id: "checkoutStepAddress",
      name: "checkoutStepAddress",
      novalidate: "",
      onSubmit: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(submit) && _unref(submit)(...args))),
      onInput: resetToAddressStep
    }, [
      _createVNode(BaseAddressForm, { onInput: resetToAddressStep }),
      _createVNode(ContactInfoForm, {
        class: "mb-x8",
        onInput: resetToAddressStep
      }),
      _createVNode(_Transition, { name: "o-anim-fade-300" }, {
        default: _withCtx(() => [
          (_unref(isAddressStep) && !_unref(isLastStep) && !_unref(isClickAndCollectOnly))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("button", {
                  type: "submit",
                  class: "c-button c-button--primary",
                  disabled: pending.value
                }, _toDisplayString(_ctx.$dictionary.pluralize(_unref(deliverableCount), _unref(labels).continueToDeliverablesButton, _unref(labels).continueToDeliverablesButtonPlural)), 9, _hoisted_5)
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ], 32),
    (_unref(isAddressStep) && _unref(isLastStep))
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(CheckoutPaymentOptions, { class: "u-valign-inline-button" })
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})